import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import PageLayout from '@/components/feature/PageLayout';
import MangaCard from '@/components/base/MangaCard';
import { mangaList, categories, reviews } from '@/mocks/manga';

const hotManga = mangaList.slice(0, 8);
const latestManga = [...mangaList].sort((a, b) =>
  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
).slice(0, 6);

const faqs = [
  {
    question: '虫虫漫画是否需要付费？',
    answer: '虫虫漫画所有漫画内容均免费阅读，无需任何付费。我们坚持为漫画爱好者提供完全免费的在线漫画阅读服务，让每个人都能轻松享受漫画的乐趣。',
  },
  {
    question: '虫虫漫画的资源更新速度如何？',
    answer: '虫虫漫画与各大漫画平台保持同步更新，最新章节上线后我们会第一时间同步。热门漫画通常可在24小时内完成更新，确保读者不会错过任何精彩内容。',
  },
  {
    question: '虫虫漫画支持哪些设备阅读？',
    answer: '虫虫漫画采用响应式设计，完美支持电脑、平板和手机等各类设备。无论您使用什么设备，都能获得流畅舒适的漫画阅读体验。',
  },
  {
    question: '如何查找我想看的漫画？',
    answer: '您可以通过网站顶部的搜索功能直接搜索漫画名称，也可以通过分类页面按类别浏览。我们提供了热血、恋爱、玄幻、搞笑、悬疑等多种分类，方便您快速找到感兴趣的漫画。',
  },
  {
    question: '虫虫漫画有哪些漫画类型？',
    answer: '虫虫漫画涵盖热血、恋爱、玄幻、搞笑、悬疑、科幻、校园、武侠、冒险等多种类型的漫画资源。无论您喜欢什么类型的漫画，都能在这里找到心仪的作品。',
  },
];

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '虫虫漫画',
  url: 'https://www.chongchongmanga.com',
  description: '虫虫漫画作为最新最全的漫画官网，提供免费在线漫画阅读服务，资源权威且全面，更新同步。',
  inLanguage: 'zh-Hans',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.chongchongmanga.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '虫虫漫画',
  url: 'https://www.chongchongmanga.com',
  description: '免费在线漫画阅读平台',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@chongchongmanga.com',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  return (
    <PageLayout>
      <SEOHead
        title="首页"
        description="虫虫漫画作为最新最全的漫画官网，提供免费在线漫画阅读服务。涵盖热血、恋爱、玄幻、搞笑、悬疑等丰富漫画资源，资源权威且全面，更新同步，是漫画爱好者信赖的官方渠道。"
        keywords="虫虫漫画,免费阅读的在线漫画,最新最全的漫画官网,免费漫画,在线漫画,国漫,热血漫画,恋爱漫画"
        canonicalUrl="https://www.chongchongmanga.com"
        schema={{
          ...websiteSchema,
          ...organizationSchema,
        }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[480px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20stylized%20manga%20illustration%20background%20with%20warm%20coral%20orange%20and%20cream%20tones%2C%20abstract%20comic%20panel%20patterns%2C%20soft%20gradient%20overlay%2C%20clean%20minimal%20design%2C%20subtle%20Japanese%20ink%20wash%20texture%2C%20no%20text%2C%20no%20characters%2C%20modern%20flat%20illustration%20style%20with%20warm%20atmosphere&width=1440&height=900&seq=hero-manga-01&orientation=landscape"
          alt="虫虫漫画 - 免费在线漫画阅读平台"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 w-full">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
            免费阅读的在线漫画
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mb-8 leading-relaxed">
            最新最全的漫画官网，涵盖热血、恋爱、玄幻、搞笑、悬疑等丰富类型。
            资源权威全面，更新同步，漫画爱好者的首选平台。
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/category/all"
              className="bg-primary-500 text-white px-8 py-3 rounded-lg font-medium text-base hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              立即阅读
            </Link>
            <Link
              to="/about"
              className="bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-3 rounded-lg font-medium text-base hover:bg-white/30 transition-all duration-300 whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-background-50 border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">10万+</div>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">漫画资源</p>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-accent-600">每日更新</div>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">同步连载</p>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">500万+</div>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">活跃读者</p>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-accent-600">完全免费</div>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">永久承诺</p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              漫画分类
            </h2>
            <p className="text-sm text-foreground-500 mt-2">探索你喜欢的漫画类型</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-4 bg-background-50 rounded-lg hover:bg-primary-50 hover:text-primary-700 transition-all duration-200 group"
              >
                <span className="w-10 h-10 flex items-center justify-center bg-background-200 rounded-lg group-hover:bg-primary-100 transition-colors">
                  <i className={`${cat.icon} text-lg text-foreground-600 group-hover:text-primary-600`}></i>
                </span>
                <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-700 whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Manga Grid */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">最受读者欢迎的漫画作品</p>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {hotManga.map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>
        </div>
      </section>

      {/* Banner CTA */}
      <section className="py-10 md:py-14 bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
            随时随地，畅享免费漫画
          </h3>
          <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-6">
            虫虫漫画支持电脑、平板和手机阅读，无论何时何地，打开浏览器即可享受精彩漫画内容。
          </p>
          <Link
            to="/category/all"
            className="inline-flex bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold text-base hover:bg-background-100 transition-all duration-300 whitespace-nowrap"
          >
            开始探索
          </Link>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                最新更新
              </h2>
              <p className="text-sm text-foreground-500 mt-1">最新章节实时同步</p>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap"
            >
              更多更新 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
            {latestManga.map((manga, index) => (
              <Link
                key={manga.id}
                to={`/article/${manga.id}`}
                className={`flex items-center gap-4 p-4 hover:bg-background-100 transition-colors ${
                  index !== 0 ? 'border-t border-background-200' : ''
                }`}
              >
                <img
                  src={manga.cover}
                  alt={manga.title}
                  className="w-12 h-16 rounded object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground-900 text-sm truncate">{manga.title}</h4>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {manga.author} · {manga.category}
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-xs text-foreground-400">
                  <span>{formatViews(manga.views)} 阅读</span>
                  <span className="text-accent-600">{manga.status}</span>
                </div>
                <div className="text-xs text-foreground-400 whitespace-nowrap">
                  更新于 {manga.updatedAt}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 mt-2">关于虫虫漫画，你可能想了解的问题</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <details key={index} className="group bg-background-50 rounded-lg border border-background-200">
                <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                  <h4 className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</h4>
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 group-open:bg-primary-100 transition-colors">
                    <i className="ri-add-line text-sm group-open:hidden"></i>
                    <i className="ri-subtract-line text-sm hidden group-open:block text-primary-600"></i>
                  </span>
                </summary>
                <div className="px-4 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              读者评价
            </h2>
            <p className="text-sm text-foreground-500 mt-2">来自漫画爱好者的真实反馈</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviews.map((review) => (
              <div key={review.id} className="bg-background-100 rounded-lg p-5 border border-background-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center text-accent-700 font-bold text-sm">
                    {review.username.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{review.username}</p>
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-xs ${i < review.rating ? 'text-yellow-500' : 'text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-accent-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4">
            立即加入虫虫漫画
          </h2>
          <p className="text-white/85 text-sm md:text-base mb-8 leading-relaxed">
            十万部漫画免费阅读，每日同步更新。热血、恋爱、玄幻、搞笑、悬疑...
            你喜欢的类型，这里全都有。打开浏览器，即刻开启漫画之旅！
          </p>
          <Link
            to="/category/all"
            className="inline-flex bg-white text-accent-600 px-10 py-4 rounded-lg font-bold text-base hover:bg-background-100 transition-all duration-300 whitespace-nowrap"
          >
            免费开始阅读
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}